import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from '../../../i18n/useTranslation';
import {
  IDLE_SESSION_POLICY,
  LAST_ACTIVITY_KEY,
  isIdleExpired,
  isOnProtectedPage,
  lastActivityAt,
  markIdleSignedOut,
  policyMinutes,
  readLastActivity,
  recordActivity,
  resetActivityTracking,
  subscribeProtectedPages,
} from '../../../lib/idleSession';
import { Dialog } from '../../ui/Dialog/Dialog';
import { Button } from '../../atoms/Button/Button';
import { Inline } from '../../atoms/Inline/Inline';
import { Stack } from '../../atoms/Stack/Stack';
import { Text } from '../../atoms/Typography/Text';
import './IdleSessionGuard.css';

/**
 * User input that counts as activity (scrolling = wheel, keys, touch or a
 * scrollbar drag; plain `scroll` is left out because pages also scroll by
 * script). Requests, polling and token refreshes never count.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
/** setTimeout's maximum delay (~24.8 days). */
const MAX_TIMER_MS = 2_147_483_647;

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Automatic sign-out after inactivity (lib/idleSession.ts). Mounted once in
 * App inside AuthProvider. While signed in it records user activity on the
 * shared idle clock; on authenticated pages it warns
 * IDLE_SESSION_POLICY.warningMs before the timeout with a countdown dialog
 * (WCAG 2.2.1 Timing Adjustable: "Stay signed in" extends the session with one
 * action) and signs out when the timeout is reached. The timer is only a
 * trigger: every check re-reads the shared clock, and the clock is checked
 * again when a hidden tab becomes visible.
 *
 * Mounted only by App (always inside AuthProvider); pages and their tests never
 * render it, so page tests that mock useAuth are unaffected.
 */
export function IdleSessionGuard() {
  const { t } = useTranslation();
  const { state, logout } = useAuth();
  const isAuthenticated = state.isAuthenticated;
  const onProtectedPage = useSyncExternalStore(subscribeProtectedPages, isOnProtectedPage, isOnProtectedPage);
  const enforcing = isAuthenticated && onProtectedPage;

  /** Epoch ms of the sign-out while the warning is shown, else null. */
  const [deadline, setDeadline] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const warningOpenRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showWarning = useCallback((at: number | null): void => {
    warningOpenRef.current = at !== null;
    setDeadline(at);
  }, []);

  /** Compares the shared clock with the policy: sign out, warn, or wait for the next check. */
  const evaluate = useCallback((): void => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
    const last = lastActivityAt();
    if (last === null) return;
    const current = Date.now();
    const signOutAt = last + IDLE_SESSION_POLICY.timeoutMs;
    if (current >= signOutAt) {
      showWarning(null);
      markIdleSignedOut();
      return;
    }
    if (!enforcing) {
      // Public page: no warning and no timed sign-out (the clock is checked again on return).
      showWarning(null);
      return;
    }
    const warnAt = signOutAt - IDLE_SESSION_POLICY.warningMs;
    if (current >= warnAt) {
      showWarning(signOutAt);
      setNow(current);
      // Tick every second for the countdown; each tick re-reads the shared clock.
      timerRef.current = setTimeout(evaluate, Math.min(1000, signOutAt - current));
      return;
    }
    showWarning(null);
    timerRef.current = setTimeout(evaluate, Math.min(warnAt - current, MAX_TIMER_MS));
  }, [enforcing, showWarning]);

  useEffect(() => {
    if (!isAuthenticated) {
      showWarning(null);
      resetActivityTracking();
      return undefined;
    }
    // Sessions from before this feature have no clock yet: it starts now.
    if (readLastActivity() === null) recordActivity({ force: true });

    const onActivity = (): void => {
      // While the warning is open only its buttons (or Escape) extend the session.
      if (!warningOpenRef.current) recordActivity();
    };
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      // Back from a hidden tab (sleep, throttled timers): check the clock first.
      if (isIdleExpired()) {
        evaluate();
        return;
      }
      onActivity();
      evaluate();
    };
    const onStorage = (event: StorageEvent): void => {
      // Activity in another tab: re-check (closes this tab's warning).
      if (event.key === LAST_ACTIVITY_KEY && event.newValue !== null) evaluate();
    };

    ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, { capture: true, passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    evaluate();

    return () => {
      ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, onActivity, { capture: true }));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [isAuthenticated, evaluate, showWarning]);

  const staySignedIn = useCallback((): void => {
    recordActivity({ force: true });
    evaluate();
  }, [evaluate]);

  const signOutNow = useCallback((): void => {
    showWarning(null);
    void logout();
  }, [logout, showWarning]);

  if (!enforcing || deadline === null) return null;

  return (
    <Dialog
      isOpen
      onClose={staySignedIn}
      title={t('auth.idle.warningTitle', { count: policyMinutes(IDLE_SESSION_POLICY.warningMs) })}
      size="sm"
      data-testid="idle-warning-dialog"
      footer={
        <Inline gap="2" justify="end" wrap>
          <Button variant="secondary" onClick={signOutNow}>
            {t('auth.idle.logoutNow')}
          </Button>
          <Button variant="primary" onClick={staySignedIn}>
            {t('auth.idle.stay')}
          </Button>
        </Inline>
      }
    >
      <Stack gap="4">
        <Text>{t('auth.idle.warningBody', { count: policyMinutes(IDLE_SESSION_POLICY.timeoutMs) })}</Text>
        <Text role="timer" size="lg" weight="semibold" className="idle-session-guard__countdown">
          {t('auth.idle.remaining', { time: formatRemaining(deadline - now) })}
        </Text>
      </Stack>
    </Dialog>
  );
}

export default IdleSessionGuard;
